import axios from 'axios';

// 根据环境配置基础URL — exported so the v5-ported pages (Interview,
// AudioInterview, SurveyInterviewEmbed) resolve the SAME backend as the rest
// of the app instead of a hardcoded host.
export const getBaseURL = () => {
  // 开发环境使用本地后端
  if (process.env.NODE_ENV === 'development') {
    console.log('[AXIOS] Using local backend: http://localhost:8000');
    return 'http://localhost:8000';
  }
  // Production: REACT_APP_API_URL (set in DigitalOcean App settings).
  // Hardened: trim trailing slashes (avoids //api/... requests) and ignore
  // unreplaced placeholder values. Falls back to this tenant's production
  // backend so a fumbled env can never ship a dead API URL again.
  const PRODUCTION_FALLBACK_API = 'https://lobster-app-mnut2.ondigitalocean.app';
  const raw = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (!raw || /your-backend/i.test(raw)) {
    return PRODUCTION_FALLBACK_API;
  }
  return raw;
}

// 简单的缓存机制
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5分钟缓存
const MAX_CACHE_SIZE = 100; // 最多缓存100条记录

const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // 本地开发时不发送凭证，避免CORS问题
  withCredentials: process.env.NODE_ENV !== 'development',
  timeout: 60000  // 增加到60秒超时，特别针对AI分析API
});

// Resolve a possibly-relative image/asset URL into an absolute one suitable
// for an <img src=...> tag, honoring the current backend base URL. Pass
// through untouched if it's already absolute (http/https/data/blob).
export const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${getBaseURL()}${url}`;
  return url;
};

// Parse a `prompt_image_url` field that can hold either a single URL string
// (legacy) or a JSON-encoded array of URLs (current). Always returns an array.
// A trimmed empty value or a value that fails to parse becomes `[]`.
export const parseImageUrls = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch { /* fall through to single-URL treatment */ }
  }
  return [trimmed];
};

// Serialize an array of URLs back into the prompt_image_url field. Single
// URL → store as plain string (keeps the field human-readable for old
// single-image questions). Multiple URLs → JSON array. Empty → null.
export const serializeImageUrls = (urls) => {
  const list = (urls || []).filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  return JSON.stringify(list);
};

// Re-exported for convenience so callers can pull it from the api client.
// Lives in its own dependency-free module (see ./errorMessage) so it remains
// unit-testable without dragging the ESM axios package through CRA's Jest.
export { extractErrorMessage } from './errorMessage';

// 为导出和长时间运行的操作增加超时时间
axiosInstance.defaults.timeout = 60000;
axiosInstance.interceptors.request.use((config) => {
  // 导出操作和长时间操作使用5分钟超时
  if (config.url && (config.url.includes('/export') || config.url.includes('/codebook-report'))) {
    config.timeout = 300000; // 5分钟
  }
  return config;
});

// 添加请求拦截器，增加连接状态日志和缓存处理
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      console.log(`[Environment] ${process.env.NODE_ENV}`);
      console.log(`[Backend] Using ${config.baseURL}`);
    }
    // 检查缓存 (只对GET请求且非敏感数据进行缓存)
    // 排除用户相关的敏感数据：auth, conversations, projects, user profile, surveys等
    if (config.method === 'get' &&
        !config.url.includes('/admin') &&
        !config.url.includes('auth') &&
        !config.url.includes('conversations') &&
        !config.url.includes('projects') &&
        !config.url.includes('/user/') &&
        !config.url.includes('notifications') &&
        !config.url.includes('surveys') &&
        !config.url.includes('survey-sessions') &&
        !config.url.includes('/api/keys')) {
      // 包含用户token在缓存键中，确保不同用户有不同的缓存
      const userToken = localStorage.getItem('token') || 'anonymous';
      const tokenHash = userToken.length > 10 ? userToken.slice(-10) : userToken; // 使用token的最后10位作为用户标识
      const cacheKey = `${config.url}${JSON.stringify(config.params || {})}_user_${tokenHash}`;
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        if (process.env.NODE_ENV === 'development') console.log(`[Cache Hit] ${config.url} for user ${tokenHash}`);
        // 创建一个Promise来模拟缓存的响应
        config._cached = true;
        config._cacheKey = cacheKey;
        config._cachedData = cached.data;
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// 改进响应拦截器，提供更详细的错误信息和缓存处理
axiosInstance.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') console.log(`[API Response] Status: ${response.status} from ${response.config.url}`);
    
    // 处理缓存响应
    if (response.config._cached) {
      return Promise.resolve({
        ...response,
        data: response.config._cachedData,
        status: 200,
        statusText: 'OK (cached)'
      });
    }      // 缓存GET请求的响应 (排除敏感数据)
    if (response.config.method === 'get' &&
        !response.config.url.includes('/admin') &&
        !response.config.url.includes('auth') &&
        !response.config.url.includes('conversations') &&
        !response.config.url.includes('projects') &&
        !response.config.url.includes('/user/') &&
        !response.config.url.includes('notifications') &&
        !response.config.url.includes('surveys') &&
        !response.config.url.includes('survey-sessions') &&
        !response.config.url.includes('/api/keys') &&
        response.status === 200) {
      // 如果缓存已满，删除最旧的条目
      if (cache.size >= MAX_CACHE_SIZE) {
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
        if (process.env.NODE_ENV === 'development') console.log(`[Cache] Removed oldest entry to maintain size limit`);
      }
      
      // 包含用户token在缓存键中，确保不同用户有不同的缓存
      const userToken = localStorage.getItem('token') || 'anonymous';
      const tokenHash = userToken.length > 10 ? userToken.slice(-10) : userToken;
      const cacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}_user_${tokenHash}`;
      cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      if (process.env.NODE_ENV === 'development') console.log(`[Cache Set] ${response.config.url} for user ${tokenHash} (${cache.size}/${MAX_CACHE_SIZE})`);
    }
    
    return response;
  },
  (error) => {
    // 处理缓存请求的错误
    if (error.config?._cached) {
      return Promise.resolve({
        data: error.config._cachedData,
        status: 200,
        statusText: 'OK (cached)',
        config: error.config
      });
    }

    if (!error.response) {
      console.error('[Connection Error] Backend server is not reachable');
      console.error('Error details:', error.message);
    } else if (error.response.status === 401 && !error.config._retry) {
      // Try to refresh the token before giving up
      var refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        error.config._retry = true;
        return axiosInstance.post('/api/auth/refresh', { refresh_token: refreshToken })
          .then(function(res) {
            var newToken = res.data.access_token;
            localStorage.setItem('token', newToken);
            error.config.headers['Authorization'] = 'Bearer ' + newToken;
            return axiosInstance(error.config);
          })
          .catch(function() {
            // Refresh failed — clear tokens and let the 401 propagate
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            return Promise.reject(error);
          });
      }
    } else {
      console.error('[API Error]', {
        status: error.response.status,
        url: error.config.url,
        baseURL: error.config.baseURL,
        data: error.response.data
      });
    }
    return Promise.reject(error);
  }
);

// 添加健康检查方法
export const checkBackendHealth = async () => {
  try {
    const response = await axiosInstance.get('/health');
    return {
      isHealthy: response.status === 200,
      environment: process.env.NODE_ENV,
      baseURL: axiosInstance.defaults.baseURL
    };
  } catch (error) {
    return {
      isHealthy: false,
      environment: process.env.NODE_ENV,
      baseURL: axiosInstance.defaults.baseURL,
      error: error.message
    };
  }
};

// 缓存管理功能
export const clearCache = () => {
  cache.clear();
  if (process.env.NODE_ENV === 'development') console.log('[Cache] Cache cleared');
};

export const getCacheSize = () => {
  return cache.size;
};

// 清理过期缓存
export const cleanExpiredCache = () => {
  const now = Date.now();
  let cleaned = 0;
  for (const [key, value] of cache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      cache.delete(key);
      cleaned++;
    }
  }
  if (cleaned > 0) {
    if (process.env.NODE_ENV === 'development') console.log(`[Cache] Cleaned ${cleaned} expired entries`);
  }
  return cleaned;
};

// 定期清理过期缓存
setInterval(cleanExpiredCache, 60000); // 每分钟清理一次

export default axiosInstance;
